import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface UseDataBySlugOptions<T> {
  data: T[];
  isLoading: boolean;
  error: any;
  slugExtractor: (item: T) => string;
  queryKeyPrefix: string;
  staleTime?: number;
  gcTime?: number;
}

/**
 * Generic hook for finding data by slug
 * Consolidates usePropertyBySlug, useBookingByShortId patterns
 */
export const useDataBySlug = <T>(
  slug: string,
  options: UseDataBySlugOptions<T>
) => {
  const {
    data = [],
    isLoading: dataLoading,
    error: dataError,
    slugExtractor,
    queryKeyPrefix,
    staleTime = 10 * 60 * 1000, // 10 minutes
    gcTime = 20 * 60 * 1000, // 20 minutes
  } = options;

  // Create a memoized lookup map for better performance
  const dataLookup = useMemo(() => {
    if (!data.length) return new Map<string, T>();
    
    const lookup = new Map<string, T>();
    data.forEach(item => {
      const itemSlug = slugExtractor(item);
      lookup.set(itemSlug, item);
    });
    return lookup;
  }, [data, slugExtractor]);

  return useQuery({
    queryKey: [queryKeyPrefix, slug],
    queryFn: () => {
      const item = dataLookup.get(slug);
      return item || null;
    },
    enabled: !!slug && data.length > 0,
    staleTime,
    gcTime,
  });
};